
import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { detectLanguage } from '@/lib/languageUtils';

// Modify the handleTemplateGeneration function to make it non-blocking:
// Add missing type definitions for required props
interface UseChatInputHandlerProps {
  onEmailGenerated: (content: string) => void;
  processMessage: (content: string, contextType?: string, metadata?: any) => Promise<any>;
  user: any;
  credits: any;
  emailConcept: string | null;
  setIsGenerating: (isGenerating: boolean) => void;
  setTemplateChangeRequiresConfirmation: (requires: boolean) => void;
  scrollToBottom: () => void;
  setEmailConcept: (concept: string | null) => void;
}

// Export the hook with a named export
export const useChatInputHandler = ({
  onEmailGenerated,
  processMessage,
  user,
  credits,
  emailConcept,
  setIsGenerating,
  setTemplateChangeRequiresConfirmation,
  scrollToBottom,
  setEmailConcept
}: UseChatInputHandlerProps) => {
  const [input, setInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleTemplateGeneration = async (concept: string) => {
    if (!concept) return;

    try {
      // Dispatch an event that we're starting template generation
      window.dispatchEvent(new CustomEvent('template-generation-started', {
        detail: { concept }
      }));
      
      // Don't block the UI with setIsGenerating(true);
      // Instead, just show a loading indicator for the template specifically
      
      // Create a temporary banner showing that template generation is in progress
      const messageId = await addTemplateGenerationMessage(concept);
      
      // Process in the background
      const result = await processMessage(
        concept,
        'email',
        {
          format: 'html',
          contextType: 'email',
          emailStage: 'template',
          isGeneratingTemplate: true
        }
      );
      
      if (result?.content) {
        onEmailGenerated(result.content);
        
        // Update the message to indicate template generation is complete
        updateTemplateGenerationMessage(messageId, true);
        
        // Dispatch an event that template generation is complete
        window.dispatchEvent(new CustomEvent('template-generation-complete', {
          detail: { content: result.content }
        }));
      } else {
        // Update the message to indicate template generation failed
        updateTemplateGenerationMessage(messageId, false);
        
        // Dispatch an event that template generation failed
        window.dispatchEvent(new CustomEvent('template-generation-failed', {
          detail: { error: 'Template generation failed' }
        }));
      }
    } catch (error) {
      console.error('Template generation failed:', error);
      // Dispatch an event that template generation failed
      window.dispatchEvent(new CustomEvent('template-generation-failed', {
        detail: { error: error instanceof Error ? error.message : 'Unknown error' }
      }));
    } finally {
      setEmailConcept(null);
    }
  };

  // Add helper functions for message management:
  const addTemplateGenerationMessage = async (concept: string): Promise<string> => {
    const message = await addMessage(
      "Generiere E-Mail-Template basierend auf dem Konzept...",
      'assistant',
      'email',
      {
        emailStage: 'template',
        isGeneratingTemplate: true
      }
    );
    
    return message?.id || '';
  };

  const updateTemplateGenerationMessage = async (messageId: string, success: boolean) => {
    if (!messageId) return;
    
    const content = success
      ? "✓ E-Mail-Template wurde erfolgreich generiert. Sie können es jetzt ansehen oder bearbeiten."
      : "⚠️ E-Mail-Template konnte nicht generiert werden. Bitte versuchen Sie es erneut.";
      
    try {
      await updateMessage(messageId, {
        content,
        metadata: {
          emailStage: 'template',
          isGeneratingTemplate: false,
          isTemplateComplete: success
        }
      });
    } catch (error) {
      console.error('Failed to update template generation message:', error);
    }
  };

  // Define missing functions referenced in the hook
  const addMessage = async (content: string, role: string, contextType: string, metadata: any) => {
    // This would need to be implemented based on your app's message handling
    console.log('Adding message:', { content, role, contextType, metadata });
    return { id: `msg-${Date.now()}` }; // Return a mock message ID
  };

  const updateMessage = async (messageId: string, updates: any) => {
    // This would need to be implemented based on your app's message handling
    console.log('Updating message:', messageId, updates);
  };

  // Handle input submission
  const handleSubmit = useCallback(async () => {
    if (!input.trim() || isSubmitting) return;

    try {
      setIsSubmitting(true);
      scrollToBottom();
      
      // Implement your message submission logic here
      console.log('Submitting input:', input);
      
      // Clear input after submission
      setInput('');
    } catch (error) {
      console.error('Error submitting input:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [input, isSubmitting, scrollToBottom]);

  // Implement the function to trigger template generation
  const triggerTemplateGeneration = async (concept: string) => {
    if (!concept || isSubmitting) return;
    
    try {
      console.log('Triggering template generation:', concept);
      await handleTemplateGeneration(concept);
      return true;
    } catch (error) {
      console.error('Error triggering template generation:', error);
      return false;
    }
  };

  return {
    input,
    setInput,
    handleSubmit,
    isSubmitting,
    triggerTemplateGeneration
  };
};

// Fix the useBuilderState issue also mentioned in the errors
let setDetectedLanguage: React.Dispatch<React.SetStateAction<import("@/types/insurance").Language>> | null = null;

export const fixBuilderState = (
  lang: string,
  setLanguage: React.Dispatch<React.SetStateAction<import("@/types/insurance").Language>>
) => {
  // Store the setter for detected language
  setDetectedLanguage = setLanguage;
  
  // Implementation of the fix that was mentioned in the errors
  if (lang === 'de' || lang === 'en' || lang === 'fr' || lang === 'it') {
    setLanguage(lang as import("@/types/insurance").Language);
  } else {
    setLanguage('de'); // Default to German if not a valid language
  }
};
